import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseNavigationGuardOptions {
  hasUnsavedChanges: boolean;
  enabled?: boolean;
}

interface UseNavigationGuardReturn {
  isBlocked: boolean;
  proceed: () => void;
  cancel: () => void;
  guardedNavigate: (path: string) => void;
  pendingPath: string | null;
}

export const useNavigationGuard = ({
  hasUnsavedChanges,
  enabled = true,
}: UseNavigationGuardOptions): UseNavigationGuardReturn => {
  const shouldBlock = enabled && hasUnsavedChanges;
  const [isBlocked, setIsBlocked] = useState(false);
  const pendingPathRef = useRef<string | null>(null);
  const navigate = useNavigate();

  // Handle browser tab close/refresh with beforeunload
  useEffect(() => {
    if (!shouldBlock) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  // Intercept link clicks
  useEffect(() => {
    if (!shouldBlock) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');

      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Only intercept internal links (not external or hash links)
      if (href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:')) {
        return;
      }

      // Prevent the navigation
      e.preventDefault();
      e.stopPropagation();

      pendingPathRef.current = href;
      setIsBlocked(true);
    };

    // Use capture phase to intercept before React Router handles it
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [shouldBlock]);

  // Handle browser back/forward buttons
  useEffect(() => {
    if (!shouldBlock) return;

    const handlePopState = () => {
      // Push current URL back to prevent leaving
      window.history.pushState(null, '', window.location.href);
      pendingPathRef.current = '__BACK__';
      setIsBlocked(true);
    };

    // Push a state entry so we can intercept back
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlock]);

  const proceed = useCallback(() => {
    const path = pendingPathRef.current;
    pendingPathRef.current = null;
    setIsBlocked(false);

    if (path === '__BACK__') {
      // Go back twice: once for our pushState, once for actual back
      window.history.go(-2);
    } else if (path) {
      navigate(path);
    }
  }, [navigate]);

  const cancel = useCallback(() => {
    setIsBlocked(false);
    pendingPathRef.current = null;
  }, []);

  // Guarded navigate function for programmatic navigation
  const guardedNavigate = useCallback((path: string) => {
    if (shouldBlock) {
      pendingPathRef.current = path;
      setIsBlocked(true);
    } else {
      navigate(path);
    }
  }, [shouldBlock, navigate]);

  return {
    isBlocked,
    proceed,
    cancel,
    guardedNavigate,
    pendingPath: pendingPathRef.current,
  };
};
